import { GuestModel, UserAuthModel, useCities, useDialog } from '@/logic/state';

import getConfig from 'next/config';
import isEmpty from 'lodash/isEmpty';
import shallow from 'zustand/shallow';
import { storage } from '../libs';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { UserModel } from '@/features/login-and-signup/types/user-model';
import { PATHS } from '@/constants/routes';
import { parseCityId } from '@/helpers/parse-city-id';

// this hook is used to display 'select city form' when enter the website for the first time

export const useSelectInitialCity = () => {
  const setDialog = useDialog(s => s.setDialog, shallow);
  const router = useRouter();
  const cities = useCities(s => s.cities, shallow);

  const { citiesList } = cities;
  const isCitiesListEmpty = isEmpty(citiesList);
  const citiesListDepsString = JSON.stringify(citiesList);

  const user: UserAuthModel | UserModel = storage?.user?.get();
  const guest: GuestModel = storage?.guest?.get();

  const userPreferredCityId = user?.preferredCityId;
  const guestPreferredCityId = guest?.preferredCityId;

  const { publicRuntimeConfig } = getConfig();

  useEffect(() => {
    if (
      publicRuntimeConfig.maintenance === 'false' &&
      router.pathname !== PATHS.UNDER_MAINTENANCE &&
      // only a usable cityId in the URL should suppress the dialog — a
      // malformed one (e.g. ?cityId=null from a mangled link) would otherwise
      // leave a first-time visitor with no way to pick a city
      !parseCityId(router.query.cityId)
    ) {
      // Every branch waits for the cities list. Opening earlier used to render
      // an all-but-empty dialog that then grew by ~550px as the list arrived,
      // and because MUI centres the Paper that growth moved both the Paper and
      // its actions — a ~0.6 layout shift, the largest single contributor to
      // CLS on a first visit. `citiesListDepsString` is already a dependency,
      // so the effect re-runs and opens the dialog once the list is there.
      if (isCitiesListEmpty) return;

      if (user && !userPreferredCityId) {
        setDialog({
          wrapper: true,
          isCitiesForm: true,
          citiesFormPayload: {
            isUser: true,
          },
        });
      } else if (!guestPreferredCityId && !userPreferredCityId) {
        setDialog({
          wrapper: true,
          isCitiesForm: true,
          citiesFormPayload: {
            isUser: false,
          },
        });
      } else if (
        userPreferredCityId &&
        !isCitiesListEmpty &&
        !citiesList.find(city => city.id === userPreferredCityId)
      ) {
        setDialog({
          wrapper: true,
          isCitiesForm: true,
          citiesFormPayload: {
            isUser: true,
          },
        });
      } else if (
        guestPreferredCityId &&
        !userPreferredCityId &&
        !isCitiesListEmpty &&
        !citiesList.find(city => city.id === guestPreferredCityId)
      ) {
        setDialog({
          wrapper: true,
          isCitiesForm: true,
          citiesFormPayload: {
            isUser: false,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    citiesListDepsString,
    publicRuntimeConfig.maintenance,
    router.pathname,
    router.query.cityId,
  ]);
};
